import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setEmail as setEmailAction } from "./userSlice";
import { getSession, getToken, clearSession, setSession } from "./utils/session";

const API_BASE =
  process.env.REACT_APP_API_URL || "https://api-project360-insights.haqdarshak.com";

/**
 * ProtectedRoute
 *
 * Client-side session state alone is NOT trusted — it can be forged (edited
 * login response, or a hand-set sessionStorage token). Before rendering a
 * protected page we ask the backend to validate the JWT. Access is granted only
 * when the server returns 200; anything else (forged token, expired token,
 * network error) fails closed and redirects to the login screen.
 */
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const session = getSession();
  const hasClientSession = !!(session && session.email && session.token);

  // "checking" | "valid" | "invalid"
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    let active = true;

    // No client session at all -> straight to login, no server call needed.
    if (!hasClientSession) {
      setStatus("invalid");
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/verify/`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        });

        if (!active) return;

        if (res.ok) {
          // The server tells us who the token actually belongs to. If the stored
          // email disagrees — a tampered login response, or a hand-edited
          // sessionStorage entry — the server's answer wins, so the app can
          // never display or act as an account the token does not own.
          try {
            const body = await res.json();
            const verifiedEmail = (body && body.email) ? String(body.email).trim() : "";
            if (verifiedEmail && verifiedEmail !== session.email) {
              setSession({ email: verifiedEmail });
              dispatch(setEmailAction(verifiedEmail));
            }
          } catch {
            /* a 200 with an unreadable body changes nothing about access */
          }

          setStatus("valid");
          try {
            sessionStorage.setItem("lastPath", location.pathname + location.search);
          } catch {
            /* ignore */
          }
        } else {
          // Forged / expired / rejected token.
          clearSession();
          setStatus("invalid");
        }
      } catch {
        // Fail closed on any error — do not grant access on uncertainty.
        if (active) {
          clearSession();
          setStatus("invalid");
        }
      }
    })();

    return () => {
      active = false;
    };
    // Re-verify whenever the route changes. `session.email` and `dispatch` are
    // deliberately not dependencies: the email correction above would otherwise
    // re-trigger this effect and fire a second verify call for no benefit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasClientSession, location.pathname, location.search]);

  if (status === "checking") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f1f5f9",
          color: "#64748b",
          fontFamily: "system-ui, sans-serif",
          fontSize: 14,
          gap: 10,
        }}
      >
        <span style={{ display: "inline-block", animation: "hdspin 1s linear infinite" }}>⟳</span>
        Verifying session…
        <style>{`@keyframes hdspin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (status === "invalid") {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute;
